import _extends from '@babel/runtime/helpers/extends';
import PropTypes from 'prop-types';
import React from 'react';
import { p as props, d as defaults } from './componentEl-c8b73b10.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-cc2dcbf1.js';
import { a as addSpacingClasses, m as marginPropTypes } from './spacing-7e57ffb0.js';
import Icon from './Icon.js';
import { B as Button } from './Button-7604c54d.js';
import { s as setIcon } from './icons-fde0c050.js';

/*
Generic component for a single toggle item, to be used in the context of Toggle
*/
function ToggleItem({
  componentEl,
  onChange,
  className,
  selected,
  children,
  disabled,
  icon,
  value,
  ...otherProps
}) {
  function localChangeHandler(e) {
    if (!selected) onChange(value, e);
  }
  const cssClasses = ['st_toggleItem'];
  if (selected) cssClasses.push('st_toggleItem--selected');
  if (disabled) cssClasses.push('st_toggleItem--disabled');
  if (className) cssClasses.push(className);
  const componentProps = {
    margin: {
      horizontal: 'xs'
    }
  };
  const iconProps = {};
  if (selected) {
    componentProps['aria-selected'] = selected;
    componentProps.active = true;
  }
  if (componentEl === 'button') {
    componentProps.disabled = disabled;
  }
  componentProps.style = {
    '--st-margin-hor-modifier': '0px'
  };

  // if this will render as a <button> and there's no explicit button type attribute set
  if ((componentEl === Button || componentEl === 'button') && !otherProps.type) {
    componentProps.type = 'button';
  }

  // conditionally pass margin
  if (children) iconProps.margin = {
    right: 'sm'
  };
  const toggleIcon = setIcon(Icon, icon, iconProps);
  return (
    /*#__PURE__*/
    // component not complex enough to warrant a React useCallback to silence this error
    // eslint-disable-next-line react/jsx-no-bind
    React.createElement(Button, _extends({
      tertiary: true,
      componentEl: componentEl,
      onClick: localChangeHandler,
      className: cssClasses.join(' ')
    }, componentProps, otherProps), toggleIcon, children)
  );
}
ToggleItem.propTypes = {
  /** additional CSS classes for outermost element, if any */
  className: PropTypes.string,
  /**
   * callback when tab is clicked, and is not currently selected.
   * Returns the value and the event by calling `onChange(value, e)`
   * Used if not defined in parent <Toggle> */
  onChange: PropTypes.func,
  /** component or DOM element to use for rendering outermost tag */
  componentEl: props,
  /** optional icon. A screentone icon component, e.g. IconStar */
  icon: PropTypes.any,
  /** whether tab is currently selected */
  selected: PropTypes.bool,
  /** value of tab, useful when tab is clicked */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** whether control is disabled */
  disabled: PropTypes.bool
};

// default props
ToggleItem.defaultProps = {
  className: null,
  onChange: null,
  icon: null,
  componentEl: 'button',
  selected: false,
  value: undefined,
  disabled: false
};

/*
Generic component for Tabs
Accepts one or more Tab component inside
Does not do any state management! See Storybook for examples for state management
*/
function Toggle({
  componentEl: Component,
  className,
  onChange,
  children,
  margin,
  value,
  ...otherProps
}) {
  let childIndex = 0;
  const childrenWithProps = React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    const childValue = typeof child.props.value === 'undefined' ? childIndex : child.props.value;
    const selected = childValue === value;
    childIndex += 1;
    return /*#__PURE__*/React.cloneElement(child, {
      selected,
      onChange,
      value: childValue
    });
  });
  let cssClasses = ['st_toggle', 'st_border'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), childrenWithProps);
}

// proptypes definitions
Toggle.propTypes = {
  /**
   * event handler when new ToggleItemis selected.
   * Returns the value and the event by calling `onChange(value, e)`
   * */
  onChange: PropTypes.func,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** currently selected Tab, either the index number of the actual value of tab */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

// default props
Toggle.defaultProps = {
  onChange: null,
  className: null,
  margin: null,
  componentEl: defaults,
  value: null
};
Toggle.Item = ToggleItem;

export { Toggle as T };
